import { useEffect, useState } from "react";
import {
  readReplyToNote,
  setReplyToNote,
  type ReplyToNote,
} from "@stockline/web";

// The assistant panel has no global store — it tracks its open state via this
// localStorage flag and the `activityPanelToggle` window event (see
// `components/ai-management-panel.tsx`). We mirror both here so a note's Reply
// button can open the panel from any surface. Keep this key in sync with
// PANEL_OPEN_KEY there.
const PANEL_OPEN_KEY = "stockline_activity_panel_open";

// The view-context replyToNote slot is a plain module var (not reactive). This
// event lets the chip above the composer re-render when the slot changes.
const REPLY_EVENT = "stockline:replyToNoteChange";

/** Set (or clear) the replyToNote slot and notify subscribers. */
export function setReplyToNoteAndNotify(note: ReplyToNote | null): void {
  setReplyToNote(note);
  window.dispatchEvent(new Event(REPLY_EVENT));
}

/** Clear the active reply-to-note (chip dismissed). */
export function clearReplyToNote(): void {
  setReplyToNoteAndNotify(null);
}

/**
 * Begin replying to an AI note: stash it as the cross-cutting `replyToNote`
 * view-context (so the next chat turns carry its content + ids) and open the
 * AI assistant panel.
 */
export function openAssistantReplyingToNote(note: ReplyToNote): void {
  setReplyToNoteAndNotify(note);
  try {
    localStorage.setItem(PANEL_OPEN_KEY, "true");
  } catch {
    // Private-mode / blocked storage — the event below still opens the panel.
  }
  window.dispatchEvent(
    new CustomEvent("activityPanelToggle", { detail: true }),
  );
}

/** Reactively read the active reply-to-note (drives the composer chip). */
export function useReplyToNote(): ReplyToNote | null {
  const [note, setNote] = useState<ReplyToNote | null>(() => readReplyToNote());
  useEffect(() => {
    const handler = () => setNote(readReplyToNote());
    window.addEventListener(REPLY_EVENT, handler);
    return () => window.removeEventListener(REPLY_EVENT, handler);
  }, []);
  return note;
}
